import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { Center, Spinner } from "native-base";
import { v4 as uuidv4 } from "uuid";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import axios from "axios";
import qs from "qs";
import * as analytics from "firebase/analytics";
import constants from "./constants";
import {
  initialState,
  initialStatus,
  StateContext,
  stateReducer,
  StatusContext,
  statusReducer,
} from "./contexts";
import API, { getErrorMessage } from "./api";
import routes from "./routes";
import Toast from "./components/toast";
import colors from "./constants/colors";

import MainLayout from "./layouts/main";
import SignUpLayout from "./layouts/signUp";
import VendorLayout from "./layouts/vendor";

import HomePage from "./pages/home";
import SignUpVendorPage from "./pages/signUp/vendor";
import SignUpAgencyRntPage from "./pages/signUp/agency/rnt";
import SignUpAgencyDataPage from "./pages/signUp/agency/data";
import SignUpAgencyDocumentsPage from "./pages/signUp/agency/documents";
import SignUpAgencyPendingPage from "./pages/signUp/agency/pending";
import SignUpAgencySuccessPage from "./pages/signUp/agency/success";
import SignUpAgencyPage from "./pages/signUp/agency";
import SignInPage from "./pages/signIn";

import AgencyDashboardPage from "./pages/agency/dashboard";
import AgencyExperienceCreationPage from "./pages/agency/experienceCreation";
import AgencyExperienceCreationDetailsPage from "./pages/agency/experienceCreation/details";
import AgencyExperienceCreationTransportSelectionPage from "./pages/agency/experienceCreation/transportSelection";
import AgencyExperienceCreationPurchaseSummaryPage from "./pages/agency/experienceCreation/purchaseSummary";
import AgencyPaymentManagementPage from "./pages/agency/paymentManagement";
import AgencyPaymentManagementInstallmentsPage from "./pages/agency/paymentManagement/installments";
import AgencyPaymentManagementPricingPage from "./pages/agency/paymentManagement/pricing";
import AgencyPaymentManagementPassengersInstallmentsPage from "./pages/agency/paymentManagement/passengersInstallments";
import AgencyProfilePage from "./pages/agency/profile";
import AgencyTripsAndPassengersPage from "./pages/agency/tripsAndPassengers";
import AgencyQuotationsPage from "./pages/agency/quotations";
import AgencyProfileBalancePage from "./pages/agency/profile/balance";
import AgencyPaymentManagementServicesPage from "./pages/agency/paymentManagement/services";
import AgencyProfileDocumentsPage from "./pages/agency/profile/documents";
import AgencyPaymentManagementServicesServicePage from "./pages/agency/paymentManagement/services/service";

import TourPage from "./pages/tour";
import TourSeatSelectionPage from "./pages/tour/seatSelection";
import TourPurchaseSummaryPage from "./pages/tour/purchaseSummary";
import SignUpPage from "./pages/signUp/index.js";
import TicketsPage from "./pages/tickets";
import PaymentResultPage from "./pages/payment/result";
import ExperienceCreationPage from "./pages/experienceCreation";
import ExperienceCreationDetailsPage from "./pages/experienceCreation/details";
import ExperienceCreationTransportSelectionPage from "./pages/experienceCreation/transportSelection";
import ExperienceCreationPurchaseSummaryPage from "./pages/experienceCreation/purchaseSummary";
import QuotationsPage from "./pages/quotations";
import ProfileLayout from "./layouts/profile";
import ProfileBalance from "./pages/profile/balance";
import TrypsPage from "./pages/trips";
import ExperienceCreationComplementsPage from "./pages/experienceCreation/complements";
import TripsInstallmentsPage from "./pages/trips/installments/index.js";

const { USER, JWT, IS_LOGGED_IN } = constants.state;
const { ERROR_MESSAGE, SUCCESS_MESSAGE, INFO_MESSAGE, WARNING_MESSAGE } =
  constants.status;

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [state, dispatchState] = useReducer(stateReducer, initialState);
  const [status, dispatchStatus] = useReducer(statusReducer, initialStatus);

  const onRefreshUser = useCallback(
    async (jwt = "") => {
      const { data: user } = await API(jwt || state.jwt).get("/users/me");
      dispatchState({ type: USER, user });
      return user;
    },
    [state.jwt],
  );

  const stateContext = useMemo(
    () => ({
      updateUser: (user) => dispatchState({ type: USER, user }),
      updateJwt: (jwt) => dispatchState({ type: JWT, jwt }),
      updateIsLoggedIn: (isLoggedIn) =>
        dispatchState({ type: IS_LOGGED_IN, isLoggedIn }),
      refreshUser: onRefreshUser,
      ...state,
    }),
    [state, onRefreshUser],
  );

  const statusContext = useMemo(
    () => ({
      updateErrorMessage: (errorMessage) => {
        if (!axios.isCancel(errorMessage)) {
          dispatchStatus({
            type: ERROR_MESSAGE,
            errorMessage: getErrorMessage(errorMessage),
          });
        }
      },
      updateSuccessMessage: (successMessage) =>
        dispatchStatus({ type: SUCCESS_MESSAGE, successMessage }),
      updateInfoMessage: (infoMessage) =>
        dispatchStatus({ type: INFO_MESSAGE, infoMessage }),
      updateWarningMessage: (warningMessage) =>
        dispatchStatus({ type: WARNING_MESSAGE, warningMessage }),
    }),
    [],
  );

  const initialize = useCallback(async () => {
    try {
      let uuid = localStorage.getItem(constants.UUID);
      if (!uuid) {
        uuid = uuidv4();
        localStorage.setItem(constants.UUID, uuid);
      }
      if (searchParams.get("jwt") !== null) {
        localStorage.setItem(JWT, searchParams.get("jwt"));
        if (searchParams.get("return_url")) {
          const url = decodeURIComponent(searchParams.get("return_url"));
          window.location.href = url;
          return;
        } else {
          setSearchParams({}, { replace: true });
        }
      }
      const jwt = sessionStorage.getItem(JWT) || localStorage.getItem(JWT);
      if (jwt) {
        const { data: user } = await API(jwt).get("/users/me");
        dispatchState({ type: JWT, jwt });
        dispatchState({ type: USER, user });
        dispatchState({ type: IS_LOGGED_IN, isLoggedIn: true });
        if (
          user?.agency &&
          !window.location.pathname.startsWith(routes.agency)
        ) {
          navigate(routes.agencyExperienceCreation);
        }
      }
    } catch (error) {
      dispatchStatus({
        type: ERROR_MESSAGE,
        errorMessage: getErrorMessage(error),
      });
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    if (!isLoading) {
      const parentWindow = window.parent;
      if (parentWindow) {
        parentWindow.postMessage(state, "*");
      }
    }
  }, [isLoading, state]);

  useEffect(() => {
    if (state.user) {
      analytics.setUserId(analytics.getAnalytics(), String(state.user.id));
      analytics.setUserProperties(analytics.getAnalytics(), state.user);
    }
  }, [state.user]);

  if (isLoading) {
    return (
      <Center flex={1}>
        <Spinner size="lg" color={colors.primary} />
      </Center>
    );
  }

  return (
    <StateContext.Provider value={stateContext}>
      <StatusContext.Provider value={statusContext}>
        <Routes>
          {!state.isLoggedIn && (
            <Route path={routes.signIn} element={<SignInPage />} />
          )}
          {!state.isLoggedIn && (
            <Route path="*" element={<Navigate to={routes.signIn} />} />
          )}
          {!state.isLoggedIn && (
            <Route
              path={routes.home}
              element={<Navigate to={routes.signIn} />}
            />
          )}
          {!state.isLoggedIn && (
            <Route path={routes.signInRole} element={<SignInPage />} />
          )}
          {!state.isLoggedIn && (
            <Route path={routes.signUp} element={<SignUpLayout />}>
              <Route
                path={routes.signUpVendor}
                element={<SignUpVendorPage />}
              />
              <Route
                path={routes.signUpAgency}
                element={<SignUpAgencyPage />}
              />
              <Route index element={<SignUpPage />} />
            </Route>
          )}
          {!state.user.isVendor && (
            <Route path={routes.home} element={<MainLayout />}>
              <Route path={routes.home} element={<HomePage />} />
              <Route path={routes.tour} element={<TourPage />} />
              <Route
                path={routes.tourSeatSelection}
                element={<TourSeatSelectionPage />}
              />
              <Route
                path={routes.tourPurchaseSummary}
                element={<TourPurchaseSummaryPage />}
              />
              <Route
                path={routes.paymentResult}
                element={<PaymentResultPage />}
              />
              <Route
                path={routes.experienceCreation}
                element={<ExperienceCreationPage />}
              />
              <Route
                path={routes.experienceCreationId}
                element={<ExperienceCreationPage />}
              />
              <Route
                path={routes.experienceCreationDetails}
                element={<ExperienceCreationDetailsPage />}
              />
              <Route
                path={routes.experienceCreationTransportSelection}
                element={<ExperienceCreationTransportSelectionPage />}
              />
              <Route
                path={routes.experienceCreationPurchaseSummary}
                element={<ExperienceCreationPurchaseSummaryPage />}
              />
              <Route
                path={routes.experienceCreationComplements}
                element={<ExperienceCreationComplementsPage />}
              />
              {state.isLoggedIn && (
                <>
                  <Route path={routes.tickets} element={<TicketsPage />} />
                  <Route
                    path={routes.quotations}
                    element={<QuotationsPage />}
                  />
                  <Route path={routes.profile} element={<ProfileLayout />}>
                    <Route
                      path={routes.profileBalance}
                      element={<ProfileBalance />}
                    />
                    <Route
                      index
                      // TODO: Reemplazar con vista de perfil de usuario cuando esté lista
                      element={<Navigate to={routes.profileBalance} />}
                    />
                  </Route>
                  <Route path={routes.trips} element={<TrypsPage />} />
                  <Route
                    path={routes.tripsInstallments}
                    element={<TripsInstallmentsPage />}
                  />
                </>
              )}
            </Route>
          )}
          {state.isLoggedIn && state.user.isVendor && (
            <Route path={routes.signUp} element={<SignUpLayout />}>
              <Route
                path={routes.signUpAgencyRnt}
                element={<SignUpAgencyRntPage />}
              />
              <Route
                path={routes.signUpAgencyData}
                element={<SignUpAgencyDataPage />}
              />
              <Route
                path={routes.signUpAgencyDocuments}
                element={<SignUpAgencyDocumentsPage />}
              />
              <Route
                path={routes.signUpAgencyPending}
                element={<SignUpAgencyPendingPage />}
              />
              <Route
                path={routes.signUpAgencySuccess}
                element={<SignUpAgencySuccessPage />}
              />
            </Route>
          )}
          {state.isLoggedIn && state.user.isVendor && (
            <Route path={routes.agency} element={<VendorLayout />}>
              <Route
                path={routes.agencyDashboard}
                element={<AgencyDashboardPage />}
              />
              <Route
                path={routes.agencyProfile}
                element={<AgencyProfilePage />}
              />
              <Route
                path={routes.agencyProfileBalance}
                element={<AgencyProfileBalancePage />}
              />
              <Route
                path={routes.agencyProfileDocuments}
                element={<AgencyProfileDocumentsPage />}
              />
              <Route
                path={routes.agencyExperienceCreation}
                element={<AgencyExperienceCreationPage />}
              />
              <Route
                path={routes.agencyExperienceCreationId}
                element={<AgencyExperienceCreationPage />}
              />
              <Route
                path={routes.agencyExperienceCreationDetails}
                element={<AgencyExperienceCreationDetailsPage />}
              />
              <Route
                path={routes.agencyExperienceCreationTransportSelection}
                element={<AgencyExperienceCreationTransportSelectionPage />}
              />
              <Route
                path={routes.agencyExperienceCreationPurchaseSummary}
                element={<AgencyExperienceCreationPurchaseSummaryPage />}
              />
              <Route
                path={routes.agencyPaymentManagement}
                element={<AgencyPaymentManagementPage />}
              />
              <Route
                path={routes.agencyPaymentManagementPricing}
                element={<AgencyPaymentManagementPricingPage />}
              />
              <Route
                path={routes.agencyPaymentManagementInstallments}
                element={<AgencyPaymentManagementInstallmentsPage />}
              />
              <Route
                path={routes.agencyPaymentManagementServices}
                element={<AgencyPaymentManagementServicesPage />}
              />
              <Route
                path={routes.agencyPaymentManagementServicesService}
                element={<AgencyPaymentManagementServicesServicePage />}
              />
              <Route
                path={routes.agencyPaymentManagementPassengersInstallments}
                element={<AgencyPaymentManagementPassengersInstallmentsPage />}
              />
              <Route
                path={routes.agencyTripsAndPassengers}
                element={<AgencyTripsAndPassengersPage />}
              />
              <Route
                path={routes.agencyQuotations}
                element={<AgencyQuotationsPage />}
              />
              <Route
                index
                element={<Navigate to={routes.agencyExperienceCreation} />}
              />
            </Route>
          )}
          {state.isLoggedIn ? (
            state.user.isVendor ? (
              <Route
                path="*"
                element={<Navigate to={routes.agencyExperienceCreation} />}
              />
            ) : (
              <Route
                path="*"
                element={<Navigate to={routes.experienceCreation} />}
              />
            )
          ) : (
            <Route
              path="*"
              element={
                <Navigate
                  to={{
                    pathname: routes.signIn,
                    search: qs.stringify({ url: location.pathname }),
                  }}
                />
              }
            />
          )}
        </Routes>
        <Toast status={{ ...statusContext, ...status }} />
      </StatusContext.Provider>
    </StateContext.Provider>
  );
}

export default App;
